import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, PackageOpen } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  buttonVariants,
  cn,
  EmptyState,
  H3,
  Input,
  Mono,
  Text,
} from "@lagoon/ui";

import { api, describeActionFailure, type ActionFailure, type Deploy } from "../api.js";
import { Notice, Working } from "./feedback.js";

/**
 * Releases, and what they would change.
 *
 * The old table showed state, file count and a timestamp, which made two
 * releases from the same afternoon indistinguishable and gave an owner nothing
 * to decide on before publishing. Rows are now numbered, carry a short id, and
 * expand to show a private preview link and a file-level diff against whatever
 * is live.
 *
 * Rollback used to open `window.prompt`, and cancelling or typing two
 * characters returned silently, so the button looked broken. The reason is now
 * an inline form that says what it is about to do and why it is refusing. It
 * stays inline rather than becoming a dialog: it belongs to one row among
 * many, and a modal over a list is how somebody loses track of which row they
 * pressed.
 *
 * Which action a row offers is its position relative to the live release, not
 * whether the site has one: anything newer publishes, anything older rolls
 * back and is asked for a reason.
 */
export function Releases(props: {
  siteId: string;
  deploys: Deploy[];
  onChanged: () => void;
}): React.ReactElement {
  const [error, setError] = useState<ActionFailure | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState<string | null>(null);

  const publish = useMutation({
    mutationFn: (deployId: string) => api.publish(props.siteId, deployId),
    onSuccess: props.onChanged,
    onError: (caught) => {
      setError(describeActionFailure(caught, "Could not publish."));
    },
  });

  const rollback = useMutation({
    mutationFn: (input: { deployId: string; reason: string }) =>
      api.rollback(props.siteId, input.deployId, input.reason),
    onSuccess: () => {
      setRollingBack(null);
      props.onChanged();
    },
    onError: (caught) => {
      setError(describeActionFailure(caught, "Could not roll back."));
    },
  });

  // Numbered oldest-first so the number never changes when a new one lands.
  const ordinals = new Map(
    props.deploys
      .toSorted((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((d, index) => [d.id, index + 1]),
  );
  // Only a release older than the live one is a rollback (LGN-124). The row
  // used to branch on whether the site had a live release at all, which made
  // every deploy after the first one, including the one just uploaded, ask for
  // a rollback reason and file a `site.rollback` audit row for a routine
  // publish. Null when nothing is live, or when whatever is live fell off the
  // end of the list, in which case everything ready is an ordinary publish.
  const live = props.deploys.find((d) => d.isPublished);
  const liveOrdinal = live === undefined ? null : (ordinals.get(live.id) ?? null);

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <H3>Releases</H3>

      {error === null ? null : (
        <Alert tone="danger">
          {/*
            A screening refusal is not a fault, so it is not phrased as one. The
            title says what happened and the message names the rules and files
            that caused it, which is the difference between "try again" and
            "remove that file".
          */}
          {error.screening ? <AlertTitle>Content screening refused this release</AlertTitle> : null}
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {props.deploys.length === 0 ? (
        <EmptyState
          icon={<PackageOpen />}
          title="Nothing deployed yet"
          description={
            <>
              Upload a ZIP above. It needs an <Mono>index.html</Mono> at the top level, not inside a
              folder.
            </>
          }
        />
      ) : (
        <ul className="lg:flex lg:flex-col lg:gap-px lg:overflow-hidden lg:rounded-lg lg:border lg:border-hairline lg:bg-hairline">
          {props.deploys.map((deploy) => {
            const ordinal = ordinals.get(deploy.id) ?? 0;
            return (
              <ReleaseRow
                key={deploy.id}
                deploy={deploy}
                ordinal={ordinal}
                isRollback={liveOrdinal !== null && ordinal < liveOrdinal}
                expanded={expanded === deploy.id}
                onToggle={() => {
                  setExpanded(expanded === deploy.id ? null : deploy.id);
                }}
                publishing={publish.isPending}
                onPublish={() => {
                  publish.mutate(deploy.id);
                }}
                rollingBack={rollingBack === deploy.id}
                onStartRollback={() => {
                  setRollingBack(deploy.id);
                }}
                onCancelRollback={() => {
                  setRollingBack(null);
                }}
                onRollback={(reason) => {
                  rollback.mutate({ deployId: deploy.id, reason });
                }}
                rollbackPending={rollback.isPending}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** How long an upload URL is good for, mirroring UPLOAD_SAS_TTL_MS. */
const UPLOAD_WINDOW_MS = 15 * 60 * 1000;

function ReleaseRow(props: {
  deploy: Deploy;
  ordinal: number;
  /** Older than what is live, so choosing it goes backwards. */
  isRollback: boolean;
  expanded: boolean;
  onToggle: () => void;
  publishing: boolean;
  onPublish: () => void;
  rollingBack: boolean;
  onStartRollback: () => void;
  onCancelRollback: () => void;
  onRollback: (reason: string) => void;
  rollbackPending: boolean;
}): React.ReactElement {
  const { deploy } = props;
  const abandoned = isAbandoned(deploy);

  return (
    <li className="lg:flex lg:flex-wrap lg:items-start lg:gap-4 lg:bg-card lg:px-5 lg:py-4">
      <div className="lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-2">
        <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
          <span className="lg:font-medium lg:text-strong">Release {props.ordinal}</span>
          <Mono className="lg:text-micro lg:text-subtle">{deploy.id.slice(0, 8)}</Mono>
          <Badge tone={toneFor(deploy, abandoned)}>{labelFor(deploy, abandoned)}</Badge>
        </span>

        <Text as="span" size="caption" tone="muted">
          {deploy.state === "ready" ? `${deploy.fileCount} files · ` : ""}
          {new Date(deploy.createdAt).toLocaleString()}
        </Text>

        {deploy.error === null ? null : <Notice tone="danger">{deploy.error}</Notice>}

        {abandoned ? (
          <Text as="span" size="caption" tone="subtle">
            The upload never arrived, so there is nothing to publish. Deploy the file again.
          </Text>
        ) : null}

        {props.expanded ? <ReleaseDetail deployId={deploy.id} /> : null}

        {props.rollingBack ? (
          <RollbackForm
            ordinal={props.ordinal}
            pending={props.rollbackPending}
            onCancel={props.onCancelRollback}
            onSubmit={props.onRollback}
          />
        ) : null}
      </div>

      <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
        {deploy.state === "ready" ? (
          <Button variant="ghost" size="sm" onClick={props.onToggle}>
            {props.expanded ? "Hide" : "Preview & changes"}
          </Button>
        ) : null}

        {deploy.isPublished ? (
          <Text as="span" size="caption" tone="subtle">
            live
          </Text>
        ) : deploy.state !== "ready" ? null : props.isRollback ? (
          props.rollingBack ? null : (
            <Button variant="ghost" size="sm" onClick={props.onStartRollback}>
              Roll back to this
            </Button>
          )
        ) : (
          <Button variant="outline" size="sm" disabled={props.publishing} onClick={props.onPublish}>
            Publish
          </Button>
        )}
      </div>
    </li>
  );
}

/**
 * The preview link and the diff, fetched only when opened.
 *
 * Both are per-release and neither is needed to render the list, and asking
 * for a preview URL assigns one, so doing it for every row on page load would
 * write to every release an owner happened to scroll past.
 */
function ReleaseDetail(props: { deployId: string }): React.ReactElement {
  const detail = useQuery({
    queryKey: ["deploy-detail", props.deployId],
    queryFn: async () => {
      const [deploy, diff] = await Promise.all([
        api.getDeploy(props.deployId),
        api.getDiff(props.deployId),
      ]);
      return { deploy, diff };
    },
  });

  if (detail.isPending) {
    return <Working label="Loading…" className="lg:mt-2" />;
  }
  if (detail.isError) {
    return <Notice tone="danger">Could not load this release.</Notice>;
  }

  const { deploy, diff } = detail.data;

  return (
    <div className="lg:mt-2 lg:flex lg:flex-col lg:gap-4">
      {deploy.previewUrl === null ? null : (
        <div className="lg:flex lg:flex-col lg:items-start lg:gap-2">
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
            href={deploy.previewUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open private preview
            <ExternalLink aria-hidden="true" />
          </a>
          <Text as="span" size="caption" tone="subtle">
            Only people who can already see this site can open it. Publishing is separate.
          </Text>
        </div>
      )}

      <div className="lg:flex lg:flex-col lg:gap-2">
        <Text as="span" size="sm" tone="strong">
          {diff.summary}
        </Text>
        {diff.files.length === 0 ? null : (
          <div className="lg:overflow-hidden lg:rounded-sm lg:border lg:border-hairline-2">
            {diff.files.slice(0, 40).map((file) => (
              <div
                key={file.path}
                className="lg:flex lg:items-center lg:gap-3 lg:border-b lg:border-hairline lg:px-3 lg:py-1.5 lg:text-caption lg:last:border-b-0"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "lg:w-3 lg:text-center lg:font-mono lg:font-bold",
                    file.change === "added"
                      ? "lg:text-success"
                      : file.change === "removed"
                        ? "lg:text-destructive"
                        : "lg:text-warning",
                  )}
                >
                  {markFor(file.change)}
                </span>
                <span className="lg:sr-only">{file.change}</span>
                <Mono className="lg:min-w-0 lg:flex-1 lg:truncate">{file.path}</Mono>
                <Mono className="lg:text-micro lg:text-subtle">{formatBytes(file.sizeBytes)}</Mono>
              </div>
            ))}
            {diff.files.length > 40 || diff.truncated ? (
              <div className="lg:px-3 lg:py-1.5 lg:text-micro lg:text-subtle">
                and {diff.added + diff.modified + diff.removed - Math.min(40, diff.files.length)}{" "}
                more
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Rollback needs a reason, and the old prompt discarded a short one in silence.
 * Inline, so the rule is visible before the button is pressed.
 */
function RollbackForm(props: {
  ordinal: number;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length > 0 && reason.trim().length < 3;

  return (
    <div className="lg:mt-2 lg:flex lg:flex-col lg:gap-2">
      <Text as="span" size="sm">
        {/*
          Not "live again": an older release may never have been live, if the
          one that is live was published over the top of it.
        */}
        Go back to <strong className="lg:text-strong">release {props.ordinal}</strong>. Everyone
        sees it within seconds.
      </Text>
      <div className="lg:flex lg:flex-wrap lg:gap-2">
        <Input
          inputSize="sm"
          className="lg:min-w-48 lg:flex-1"
          value={reason}
          autoFocus
          onChange={(event) => {
            setReason(event.target.value);
          }}
          placeholder="Why are you rolling back?"
          aria-label="Reason for rolling back"
        />
        <Button
          variant="destructive"
          size="sm"
          disabled={props.pending || reason.trim().length < 3}
          onClick={() => {
            props.onSubmit(reason.trim());
          }}
        >
          {props.pending ? "Rolling back…" : "Roll back"}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onCancel} disabled={props.pending}>
          Cancel
        </Button>
      </div>
      <Text as="span" size="caption" tone={tooShort ? "danger" : "subtle"}>
        {tooShort
          ? "A few more characters: this is recorded in the audit trail."
          : "Recorded in the audit trail, so it needs at least 3 characters."}
      </Text>
    </div>
  );
}

/**
 * A deploy whose upload never arrived.
 *
 * The row is created when the upload URL is issued, before any bytes exist, so
 * a closed tab or a failed PUT leaves it `pending` forever. Presented as
 * finished rather than working, which is what stops the page polling it and an
 * owner waiting on it.
 */
function isAbandoned(deploy: Deploy): boolean {
  if (deploy.state !== "pending" && deploy.state !== "uploading") return false;
  return Date.now() - new Date(deploy.createdAt).getTime() > UPLOAD_WINDOW_MS;
}

/** The badge tone for a release, in the same order the old chip classes were. */
function toneFor(deploy: Deploy, abandoned: boolean): "success" | "danger" | "neutral" | "accent" {
  if (deploy.isPublished) return "success";
  if (deploy.state === "failed") return "danger";
  if (abandoned) return "danger";
  if (deploy.state === "ready") return "neutral";
  return "accent";
}

function labelFor(deploy: Deploy, abandoned: boolean): string {
  if (deploy.isPublished) return "live";
  if (abandoned) return "never uploaded";
  if (deploy.state === "pending" || deploy.state === "uploading") return "waiting for upload";
  return deploy.state;
}

function markFor(change: "added" | "removed" | "modified"): string {
  if (change === "added") return "+";
  if (change === "removed") return "−";
  return "~";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}
